import ProductVariant, { ProductVariantModel } from '~/models/productVariant';
import { CartApiItem, FutureDeliveries } from '~/constants/types/norce';
import { Flag } from '~/constants/types/algolia';
import { NorceFlagCodes } from '~/constants/norceCodes';
import useStripFlagSuffix from '~/composeables/useStripFlagSuffix';
import usePackageAreaConverter from '~/composeables/usePackageAreaConverter';
import { useUiStore } from '~/store/ui';
import { useGlobalContentStore } from '~/store/globalContent';
import useApiFetch from '~/composeables/useApiFetch';
import useLocalDate from '~/composeables/useLocalDate';
import useWarehouse from '~/composeables/useWarehouse';
import NP from 'number-precision';

const { stripFlagSuffix } = useStripFlagSuffix();

export type StockLabel = {
  text: string,
  textShort: string,
  areaText?: string, // if converted to area
  color: string,
  borderColor: string,
  type: 'preorder' | 'ondemand' | 'expired' | 'tempSoldout' | 'soldout' | 'active' | '',
  disableBuyButton?: boolean,
}

export default function useStockStatus() {
  const { $t } = useNuxtApp();
  const globalContent = useGlobalContentStore();
  const { getVariantStockValue, getVariantStockStatus } = useWarehouse();
  const isUSMarket = globalContent.getCountryCode === 'us';
  const { getLocaleDate } = useLocalDate();

  const cartItemIncomingDate = (item: CartApiItem) => {
    const today = new Date().toISOString().split('T')[0];
    const dates = [item.stockStatus]
      .filter((f) => f.estimatedDelivery && f.estimatedDelivery > today)
      .map((m) => m?.estimatedDelivery?.split(' ')[0])
      .sort();
    if (!dates.length) {
      return null;
    }
    return dates.shift();
  };

  const getStockLabelForCartItem = (item: CartApiItem): StockLabel => {
    const { convertPackageToArea, shouldUseArea, unitDisplay } = usePackageAreaConverter(toRef(()=>item));

    if (item.onHandValue < 1 && item.stockStatus?.estimatedDelivery) {
      return {
        text: $t('stock.tempSoldout', { date: item.stockStatus?.estimatedDelivery }),
        textShort: $t('stock.tempSoldout', { date: item.stockStatus?.estimatedDelivery }),
        color: 'bg-warning',
        borderColor: 'border-warningStrong',
        type: 'tempSoldout',
      };
    } else if (item.flags.filter((flag: Flag) => stripFlagSuffix(flag.type) === NorceFlagCodes.PreOrder)?.length > 0) {
      return {
        text: $t('stock.releaseDate', { date: cartItemIncomingDate(item) }),
        textShort: $t('stock.releaseDateWithLineBreak'),
        color: 'bg-warning',
        borderColor: 'border-warningStrong',
        type: 'preorder',
      };
    } else if (item.flags.filter((flag: Flag) => stripFlagSuffix(flag.type) === NorceFlagCodes.OnDemand)?.length > 0 && item.onHandValue < 1) {
      return {
        text: item.stockStatus?.leadtimeDayCount ? $t('stock.isOnDemandProduct', { num: item.stockStatus?.leadtimeDayCount }) : $t('stock.onDemandProduct'),
        textShort: item.stockStatus?.leadtimeDayCount ? $t('stock.isOnDemandProductWithLineBreak') : $t('stock.onDemandProduct'),
        color: 'bg-warning',
        borderColor: 'border-warningStrong',
        type: 'ondemand',
      };
    } else if (item.onHandValue < 1) {
      return {
        text: $t('stock.soldout'),
        textShort: $t('stock.soldoutShort'),
        color: 'bg-errorStrong',
        borderColor: 'transparent',
        type: 'soldout',
      };
    } else if (item.quantity <= item.onHandValue) {
      let areaText = '';
      if (shouldUseArea.value) {
        const area = convertPackageToArea(item.onHandValue) || 0;
        if (isUSMarket) {
          // US market thresholds in sq ft
          if (area >= 25000) {
            areaText = $t('stock.area.more25000', { unit: unitDisplay });
          } else if (area >= 10000) {
            areaText = $t('stock.area.more10000', { unit: unitDisplay });
          } else if (area >= 5000) {
            areaText = $t('stock.area.more5000', { unit: unitDisplay });
          } else if (area >= 2500) {
            areaText = $t('stock.area.more2500', { unit: unitDisplay });
          } else {
            areaText = $t('stock.area.less2500', { area, unit: unitDisplay });
          }
        } else {
          // Other markets thresholds in sq m
          if (area >= 2500) {
            areaText = $t('stock.area.more100', { unit: unitDisplay });
          } else if (area >= 1000) {
            areaText = $t('stock.area.more100', { unit: unitDisplay });
          } else if (area >= 500) {
            areaText = $t('stock.area.more100', { unit: unitDisplay });
          } else if (area >= 250) {
            areaText = $t('stock.area.more100', { unit: unitDisplay });
          } else if (onHandArea >= 100) {
            text = $t('stock.area.more100', { unit: unitDisplay });
          } else {
            areaText = $t('stock.area.less250', { area, unit: unitDisplay });
          }
        }
      }

      return {
        text: $t('stock.inStock'),
        textShort: $t('stock.inStock'),
        areaText,
        color: 'bg-successStrong',
        borderColor: 'transparent',
        type: 'active',
      };
    }

    return { text: '', textShort: '', color: '', borderColor: '', type: '' };
  };

  const getStockLabel = (
    variant: ProductVariantModel | ProductVariant,
    detailedInstock = false,
    alternativeText = false, // category uses alternative texts
    usePrimaryStockStatus = false // use the primary stock status, i.e the variant with the highest stock status
  ): StockLabel => {

    const { convertPackageToArea, shouldUseArea, unitDisplay } = usePackageAreaConverter(toRef(()=>variant));

    const warehouseStockValue = getVariantStockValue(variant);
    const warehouseStockStatus = getVariantStockStatus(variant);

    const stockValue = usePrimaryStockStatus ?
      (variant.stockValuePrimary > 0 ? variant.stockValuePrimary : warehouseStockValue) :
      warehouseStockValue;

    // Calculate incoming date from warehouse stock status
    const today = new Date().toISOString().split('T')[0];
    const warehouseIncomingDate = warehouseStockStatus?.estimatedDelivery && warehouseStockStatus.estimatedDelivery > today
      ? warehouseStockStatus.estimatedDelivery.split('T')[0]
      : null;
    const warehouseHasIncomingDate = warehouseStockStatus?.estimatedDelivery && warehouseStockStatus.estimatedDelivery > today;

    const incomingDate = usePrimaryStockStatus ? variant.incomingDatePrimary : warehouseIncomingDate;
    const hasIncomingDate = usePrimaryStockStatus ? variant.hasIncomingDatePrimary : warehouseHasIncomingDate;
    const isTempSoldOut = usePrimaryStockStatus ? variant.isTempSoldOutPrimary : (variant.isActive && warehouseStockValue < 1 && warehouseHasIncomingDate);
    const isSoldOut = usePrimaryStockStatus ? variant.isSoldOutPrimary : (!warehouseStockValue && !warehouseIncomingDate);
    if (variant.isPreOrderProduct) {
      return {
        text: $t('stock.releaseDate', { date: incomingDate ? getLocaleDate(incomingDate, { month: 'long', day: 'numeric' }) : incomingDate }),
        textShort: $t('stock.releaseDateWithLineBreak'),
        color: 'bg-errorStrong',
        borderColor: 'border-warningStrong',
        type: 'preorder',
        disableBuyButton: false,
      };
    } else if (variant.isOnDemandProduct) {
      // Check if there's stock in the appropriate warehouse for the country
      const anyInStock = warehouseStockValue > 0;
      // If there are any in stock, this overrides "on demand"
      if (anyInStock) {
        return {
          text: $t('stock.inStock'),
          textShort: $t('stock.inStock'),
          color: 'bg-successStrong',
          borderColor: 'transparent',
          type: 'active',
          disableBuyButton: false,
        };
      } else {
        return {
          text: warehouseStockStatus?.leadTimeDayCount ? $t('stock.isOnDemandProduct', { num: warehouseStockStatus?.leadTimeDayCount }) : $t('stock.isOnDemandProduct.unknownDelivery'),
          textShort: warehouseStockStatus?.leadTimeDayCount ? $t('stock.isOnDemandProductWithLineBreak', { num: warehouseStockStatus?.leadTimeDayCount }) : $t('stock.isOnDemandProduct.unknownDelivery'),
          color: 'bg-errorStrong',
          borderColor: 'border-warningStrong',
          type: 'ondemand',
          disableBuyButton: false,
        };
      }
    } else if (variant.productHasExpired) {
      return {
        text: $t('stock.productHasExpired'),
        textShort: $t('stock.productHasExpired'),
        color: 'bg-errorStrong',
        borderColor: 'transparent',
        type: 'expired',
        disableBuyButton: true,
      };
    } else if (isTempSoldOut && stockValue < 1) {
      return {
        text: $t('stock.tempSoldout', { date: incomingDate ? getLocaleDate(incomingDate, { month: 'long', day: 'numeric' }) : incomingDate }),
        textShort: $t('stock.tempSoldoutShort'),
        color: 'bg-errorStrong',
        borderColor: 'border-warningStrong',
        type: 'tempSoldout',
        disableBuyButton: false,
      };
    } else if ((variant.isActive || variant.isExpiring) && stockValue >= 1) {
      let text = $t('stock.inStock');
      let color = 'bg-successStrong';
      if (detailedInstock && !alternativeText) {
        if (stockValue >= 100) {
          text = $t('stock.std.over100');
        }
        else if (stockValue >= 50) {
          text = $t('stock.std.over50');
        }
        else if (stockValue >= 25) {
          text = $t('stock.std.over25');
        }
        else {
          text = $t('stock.std.less25');
          color = 'attentionPrimary';
        }
      }
      if (detailedInstock && alternativeText) {
        if (stockValue >= 5) {
          text = $t('stock.alt.over5');
        }
        else {
          text = $t('stock.alt.less5');
          color = 'attentionPrimary';
        }
      }

      let areaText = '';
      if (shouldUseArea.value) {

        const area = convertPackageToArea(warehouseStockValue) || 0;
        if (isUSMarket) {
          // US market thresholds in sq ft
          if (area >= 25000) {
            areaText = $t('stock.area.more25000', { unit: unitDisplay });
          } else if (area >= 10000) {
            areaText = $t('stock.area.more10000', { unit: unitDisplay });
          } else if (area >= 5000) {
            areaText = $t('stock.area.more5000', { unit: unitDisplay });
          } else if (area >= 2500) {
            areaText = $t('stock.area.more2500', { unit: unitDisplay });
          } else {
            areaText = $t('stock.area.less2500', { area, unit: unitDisplay });
          }
        } else {
          // Other markets thresholds in sq m
          if (area >= 2500) {
            areaText = $t('stock.area.more100', { unit: unitDisplay });
          } else if (area >= 1000) {
            areaText = $t('stock.area.more100', { unit: unitDisplay });
          } else if (area >= 500) {
            areaText = $t('stock.area.more100', { unit: unitDisplay });
          } else if (area >= 250) {
            areaText = $t('stock.area.more100', { unit: unitDisplay });
          } else if (area >= 100) {
            text = $t('stock.area.more100', { unit: unitDisplay });
          } else {
            areaText = $t('stock.area.less250', { area, unit: unitDisplay });
          }
        }

      }

      //text += variant.stockValue;
      return {
        text,
        textShort: $t('stock.inStock'),
        areaText,
        color,
        borderColor: 'transparent',
        type: 'active',
        disableBuyButton: false,
      };
    } else if (variant.isActive && isSoldOut && !hasIncomingDate) {
      return {
        text: $t('stock.soldout'),
        textShort: $t('stock.soldoutShort'),
        color: 'bg-errorStrong',
        borderColor: 'border-warningStrong',
        type: 'soldout',
        disableBuyButton: true,
      };
    }

    return { text: '', textShort: '', color: '', borderColor: '', type: '' };
  };

  return {
    getStockLabelForCartItem,
    getStockLabel,
  };
}

export type FutureDeliveriesDisplayRow = {
  color: 'green' | 'red' | 'yellow',
  text: string,
}
export type FutureDeliveriesDisplay = {
  display: boolean,
  lockBuyButton: boolean,
  rows?: FutureDeliveriesDisplayRow[],
  totalAvailable?: string | number,
  unitDisplay?: string,
}

/*
This is intentionaly left as a stand alone function and not a part of the "composeable"
 */
export const checkForFutureDelivery = async(
  partNo: string,
  totalAreaNeeded: number, // usually a combination of in cart + addition
  onHandArea: number, // onhand - from norce
  shouldUseArea: boolean, // if area conversion is used
  unitDisplay: string, // exampel m2
  isExpiring: boolean // if NorceProductStatusCodes.Expiring
): Promise<FutureDeliveriesDisplay> => {
  const { $t } = useNuxtApp();
  const uiStore = useUiStore();
  const { apiGet } = useApiFetch();
  const { getLocaleDate } = useLocalDate();
  const globalContent = useGlobalContentStore();
  const isUSMarket = globalContent.getCountryCode === 'us';

  let lockBuyButton = false;
  let outputRows = [] as FutureDeliveriesDisplayRow[];
  let exactFirstRow = null as FutureDeliveriesDisplayRow | null;

  if (onHandArea > 0) {
    let text = '';

    // We can later add different steps for "m2", "RunningFeet" etc
    if (shouldUseArea) {
      if (isUSMarket) {
        // US market thresholds in sq ft
        if (onHandArea >= 25000) {
          text = $t('stock.area.more25000', { unit: unitDisplay });
        } else if (onHandArea >= 10000) {
          text = $t('stock.area.more10000', { unit: unitDisplay });
        } else if (onHandArea >= 5000) {
          text = $t('stock.area.more5000', { unit: unitDisplay });
        } else if (onHandArea >= 2500) {
          text = $t('stock.area.more2500', { unit: unitDisplay });
        } else {
          text = $t('stock.area.less2500', { area: onHandArea, unit: unitDisplay });
        }
        exactFirstRow = {
          color: 'green',
          text: $t('stock.area.less2500', { area: onHandArea, unit: unitDisplay }),
        } as FutureDeliveriesDisplayRow;
      } else {
        // Other markets thresholds in sq m
        if (onHandArea >= 2500) {
          text = $t('stock.area.more100', { unit: unitDisplay });
        } else if (onHandArea >= 1000) {
          text = $t('stock.area.more100', { unit: unitDisplay });
        } else if (onHandArea >= 500) {
          text = $t('stock.area.more100', { unit: unitDisplay });
        } else if (onHandArea >= 250) {
          text = $t('stock.area.more100', { unit: unitDisplay });
        } else if (onHandArea >= 100) {
          text = $t('stock.area.more100', { unit: unitDisplay });
        } else {
          text = $t('stock.area.less250', { area: onHandArea, unit: unitDisplay });
        }
        exactFirstRow = {
          color: 'green',
          text: $t('stock.area.less250', { area: onHandArea, unit: unitDisplay }),
        } as FutureDeliveriesDisplayRow;
      }

    } else {
      // rounding for "pcs" and unknown formats
      if (onHandArea >= 100) {
        text = $t('stock.std.over100');
      } else if (onHandArea >= 50) {
        text = $t('stock.std.over50');
      } else if (onHandArea >= 25) {
        text = $t('stock.std.over25');
      } else if (onHandArea < 5) {
        text = $t('stock.std.less5');
      } else  {
        text = $t('stock.std.less25');
      }

      exactFirstRow = {
        color: 'green',
        text : $t('stock.std.exact', { num: onHandArea }),
      } as FutureDeliveriesDisplayRow;
    }

    outputRows.push({
      color: 'green',
      text,
    });
  }

  if (onHandArea >= totalAreaNeeded) {
    // If the product is not expiring, its safe to return here
    if (!isExpiring) {
      return {
        display: true,
        rows: outputRows,
        lockBuyButton,
      };
    }
  }

  const res = await apiGet<FutureDeliveries>(
    `/products/${partNo}/availability/${totalAreaNeeded}`
  );

  if (!res) {
    console.warn('Not loaded');
    uiStore.setTemporaryError($t('stock.apiError'));
  }

  if (res && res.date) {
    // Replaces first row with the exact number
    if (exactFirstRow) {
      outputRows[0] = exactFirstRow;
    }

    // Special for expiring product because they always call the api
    // otherwise we get 2 rows with them same content
    if (isExpiring && res.totalAvailable >= totalAreaNeeded) {
      outputRows = [{
        color: 'green',
        text: isUSMarket
          ? $t('stock.area.less2500', { area: onHandArea, unit: unitDisplay })
          : $t('stock.area.less250', { area: onHandArea, unit: unitDisplay }),
      }];
    } else {
      outputRows.push({
        color: 'yellow',
        text: $t('stock.futuredelivery', {
          num: `${NP.minus(totalAreaNeeded, onHandArea)}${unitDisplay}`,
          date: getLocaleDate(res.date, { month: 'long', day: 'numeric' }),
        }),
      });
    }
  } else {
    // Not enough coming in
    if (isExpiring) {
      if (exactFirstRow) {
        outputRows[0] = exactFirstRow;
      }
      // Product is expiring, but there will be some more production
      if (onHandArea < res.totalAvailable) {
        outputRows.push({ color: 'red', text: $t('stock.expiring.max', {
          num: res.totalAvailable,
          unit: unitDisplay,
        }),
        });
      } else {
        outputRows.push({ color: 'red', text: $t('stock.expiring') });
      }
      lockBuyButton = true;

    } else {
      outputRows = [ { color: 'red', text: $t('stock.noFutureAvaliability') }];
    }
  }
  return {
    display: true,
    rows: outputRows,
    lockBuyButton,
    totalAvailable: res.totalAvailable,
    unitDisplay,
  };
};
